import { useMemo } from 'react';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { jsonStringify } from '@lyearn/core/utils/json';
import AsyncView from '@/components/AsyncView';
import ErrorState from '@/components/Error';
import useSearchParams from '@/hooks/useSearchParam';
import { ResourceStatusEnum } from '@/types/schema';

import { PREVIEW_STATUS_KEY } from '../../../QuizDevicePreview/consts';
import useQuizPlayerData from '../../hooks/useQuizPlayerData';
import getFetchCorrectAnswers from '../../utils/getFetchCorrectAnswers';
import QuizPlayerReviewContent from './components/QuizPlayerReviewContent';
import {
  CorrectAnswersType,
  QuestionAttemptType,
} from './components/QuizPlayerReviewContent/types';
import { QuizPlayerAttemptReviewProps, QuizPlayerReviewProps } from './types';
import { getAttemptFromId, getAttemptFromQuestionPerformance, isReviewDataValid } from './utils';

const QuizPlayerAttemptReview = (props: QuizPlayerAttemptReviewProps) => {
  const {
    attemptId,
    isPreview,
    quiz,
    quizPerformance,
    questionPerformanceById,
    userId,
    className,
    isEmbed,
  } = props;

  const quizAttempt = useMemo(
    () => getAttemptFromId(attemptId, quizPerformance),
    [attemptId, quizPerformance],
  );
  const fetchCorrectAnswers = useMemo(
    () =>
      getFetchCorrectAnswers(
        quiz?.settings?.revealAnswers,
        quizPerformance,
        quiz?.settings?.retakeAllowedCount,
      ),
    [quiz?.settings?.revealAnswers, quiz?.settings?.retakeAllowedCount, quizPerformance],
  );
  const { questionAttemptById, correctAnswersByQuestionId } = useMemo(() => {
    return Object.values(questionPerformanceById).reduce<{
      questionAttemptById: Record<string, QuestionAttemptType>;
      correctAnswersByQuestionId: Record<string, CorrectAnswersType>;
    }>(
      (accumulator, questionPerformance) => {
        const attempt = getAttemptFromQuestionPerformance(questionPerformance, attemptId);
        accumulator.questionAttemptById[questionPerformance.entityId] = attempt;

        if (questionPerformance.correctOptions) {
          accumulator.correctAnswersByQuestionId[questionPerformance.entityId] = {
            correctOptions: questionPerformance.correctOptions,
            correctMTFPairings: questionPerformance.correctMTFPairings,
            correctFTBAnswers: questionPerformance.correctFTBAnswers,
            correctImageHotspotAnswers: questionPerformance.correctImageHotspotAnswers,
          };
        }

        return accumulator;
      },
      { questionAttemptById: {}, correctAnswersByQuestionId: {} },
    );
  }, [attemptId, questionPerformanceById]);

  if (!quizAttempt) {
    return <ErrorState />;
  }

  const { isValid, attemptsInfo, correctAnswersCount, quizAttemptCorrectAnswersCount } =
    isReviewDataValid({ content: quiz, quizAttempt, questionAttemptById });

  if (!isValid) {
    ErrorReporter.info('[Quiz] Invalid Data for Review Screen', {
      tags: { quizId: quiz.id, userId },
      extra: {
        attemptId,
        performancesInfo: jsonStringify(attemptsInfo).data,
        correctAnswersCount,
        quizAttemptCorrectAnswersCount,
      },
    });

    return <ErrorState />;
  }

  return (
    <QuizPlayerReviewContent
      className={className}
      correctAnswersByQuestionId={correctAnswersByQuestionId}
      fetchCorrectAnswers={fetchCorrectAnswers}
      isEmbed={isEmbed}
      isPreview={isPreview}
      questionAttemptById={questionAttemptById}
      quiz={quiz}
      quizAttempt={quizAttempt}
      quizPerformance={quizPerformance}
      quizUser={quizPerformance.user}
    />
  );
};

const QuizPlayerReview = (props: QuizPlayerReviewProps) => {
  const { attemptId, isPreview, userId, renderHeader, className, isEmbed } = props;

  const versionStatus = useSearchParams(PREVIEW_STATUS_KEY) as ResourceStatusEnum;
  const { fetching, stale, error, quizPerformance, questionPerformanceById, quiz } =
    useQuizPlayerData({
      ...props,
      versionStatus:
        isPreview && versionStatus === ResourceStatusEnum.Draft ? versionStatus : undefined,
      previewFilter: {
        isPreview: isPreview ?? false,
      },
    });

  return (
    <AsyncView
      data={quiz}
      error={error}
      fetching={fetching || stale}
      renderData={(quizContent) => (
        <>
          {renderHeader?.(quizContent)}
          <QuizPlayerAttemptReview
            attemptId={attemptId}
            className={className}
            isEmbed={isEmbed}
            isPreview={isPreview}
            questionPerformanceById={questionPerformanceById}
            quiz={quizContent}
            quizPerformance={quizPerformance!}
            userId={userId}
          />
        </>
      )}
    />
  );
};

export default QuizPlayerReview;
